import React, {Fragment, useState, useEffect} from "react";
import Card from "components/Cards/Card";
import trendScoresIcon from "assets/Icon_Tab_09.png";
import {Dropdown, SelectOption} from "components/ui/Dropdown";
import {v4 as uuidv4} from "uuid";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import LoadingIndicator from "../../ui/LoadingIndicator";

type TrendScoresProps = {
  href: string | null;
  data: ChartData;
  defaultChartType: string;
  questions: Questions[];
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean; // Add loading prop
};

type Questions = {
  value: string;
  label: string;
};

type ChartData = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage";
  };
  questions?: string[];
  periods?: string[];
};

const chartColours = [
  "#307bb9",
  "#30b862",
  "#a440a4",
  "#d5a438",
  "#bb4040",
  "#008272",
  "#4068b4",
  "#7ebce6",
  "#8fba34",
  "#f64250",
  "#fbbf22",
  "#94adbf",
];

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

function avgFormatter(item: any) {
  return item.toFixed(1);
}

const TrendTick: React.FC<any> = ({
                                    payload: {value},
                                    verticalAnchor,
                                    visibleTicksCount,
                                    ...rest
                                  }) => (
    <Text {...rest} verticalAnchor={verticalAnchor} visibleTicksCount={visibleTicksCount} width={50}>
      {value}
    </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];


const TrendScores: React.FC<TrendScoresProps> = ({
                                                   href,
                                                   data,
                                                   defaultChartType,
                                                   questions,
                                                   customName,
                                                   setImages,
                                                   idx,
                                                   isLoading = true // Default to false
                                                 }) => {
  const [trendScoresChartType, setTrendScoresChartType] = useState(
      defaultChartType ? defaultChartType : "Area"
  );
  // Track internal loading state
  const [internalLoading, setInternalLoading] = useState(true);

  // Effect to check if data is ready
  useEffect(() => {
    if (data && data.otherChartData) {
      // Set a small delay to prevent flashing
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  // Determine if we should show loading state
  const showLoading = isLoading || internalLoading;

  let trendScoresQuestions: string = "";
  let trendScoresCategories = [
    {
      label: "",
      value: "",
    },
  ];

  const [trendScoresCategory, setTrendScoresCategory] = useState<SelectOption>(
      trendScoresCategories[0]
  );

  const handleTrendScoresCategorySelect = (selectedOption: SelectOption) => {
    setTrendScoresCategory(selectedOption);
  };

  const handleSetTrendScoresChartType = (value: string) => {
    setTrendScoresChartType(value);
  };

  if (questions) {
    trendScoresCategories = [];
    questions.forEach((q: any) => {
      trendScoresCategories.push(q);
    });
    trendScoresCategories.push({
      label: "All categories",
      value: "All",
    });
    if (trendScoresCategory.label == "")
      setTrendScoresCategory(trendScoresCategories[0])

    const questionsAsObject: Record<string, string> = {};
    for (const {label, value: key} of questions) {
      questionsAsObject[key] = label;
    }
    trendScoresQuestions = JSON.stringify(questionsAsObject);
  }

  const uuid = uuidv4();

  const [getPng, {ref}] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        // Verify that png is not undefined
        if (png && setImages && idx) {
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      } catch (error) {
        // console.log(error)
      }
    }, 1000)
  }

  // If still loading, show the loading indicator regardless of data state
  if (showLoading) {
    return (
        <Card
            href={href}
            icon={trendScoresIcon}
            title={customName ? customName : "TREND SCORES"}
            chartOptions={
              <React.Fragment>
                <button className="Option" onClick={() => handleSetTrendScoresChartType("Area")}>
                  Area
                </button>
                <button className="Option" onClick={() => handleSetTrendScoresChartType("Bar")}>
                  Bar
                </button>
                <button className="Option" onClick={() => handleSetTrendScoresChartType("Line")}>
                  Line
                </button>
              </React.Fragment>
            }
        >
          <div className="CardBody">
            <div className="CategoryDropdown">
              <Dropdown
                  name="TrendCategory"
                  value={trendScoresCategory}
                  options={trendScoresCategories}
                  type="Category"
                  onChange={handleTrendScoresCategorySelect}
              />
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <LoadingIndicator />
            </div>
          </div>
        </Card>
    );
  }

  // Hide the entire component if we have less than 2 data points (after loading)
  if (data?.otherChartData.length < 2) {
    // 'Trendscoreschart' to be suppressed when only one data grouping
    return <Fragment/>
  }

  return (
      <Card
          href={href}
          icon={trendScoresIcon}
          title={customName ? customName : "TREND SCORES"}
          chartOptions={
            <React.Fragment>
              <button className="Option" onClick={() => handleSetTrendScoresChartType("Area")}>
                Area
              </button>
              <button className="Option" onClick={() => handleSetTrendScoresChartType("Bar")}>
                Bar
              </button>
              <button className="Option" onClick={() => handleSetTrendScoresChartType("Line")}>
                Line
              </button>
            </React.Fragment>
          }
      >
        <div className="CardBody">
          <div className="CategoryDropdown">
            <Dropdown
                name="TrendCategory"
                value={trendScoresCategory}
                options={trendScoresCategories}
                type="Category"
                onChange={handleTrendScoresCategorySelect}
            />
          </div>
          <div className="Chart"  style={{height: "300px"}}>
            <div className="ChartWrapper">
              {data && checkNullChartData(data.pieChartData.data) ? (
                  <div
                      style={{
                        minWidth:
                        // 100px per period (xaxis label) for area and line
                        // 50px * questions * periods for bar, unless only one question selected
                        // from the category dropdown, in which case just 100px * periods
                        // always a minimum 100px per period to ensure space for xaxis labels
                            `${((data.questions && trendScoresChartType === "Area") ||
                            trendScoresChartType === "Line"
                                ? 100
                                : data.questions && data.questions.length > 1
                                    ? trendScoresCategory.value === "All"
                                        ? data.questions.length * 50
                                        : 100
                                    : 100) * data.pieChartData.periods.length -2}px`,
                      }}
                  >
                    <ResponsiveContainer width="99%" height={270} key={uuid}>
                      {trendScoresChartType === "Area" ? (
                          <AreaChart
                              data={data.otherChartData}
                              margin={{top: 20, bottom: 20, right: 20}}
                              ref={ref}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3"/>
                            <Tooltip
                                formatter={
                                  data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : avgFormatter
                                }
                                allowEscapeViewBox={{x: true, y: true}}
                                coordinate={{x: 0, y: 300}}
                            />
                            <XAxis
                                dataKey="name"
                                tick={<TrendTick/>}
                                axisLine={false}
                                tickLine={false}
                                padding={{left: 50, right: 50}}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={
                                  data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : avgFormatter
                                }
                                domain={data.options.yAxisType === "Percentage" ? [0, 100] : undefined}
                                ticks={data.options.yAxisType === "Percentage" ? ticks20 : undefined}
                            />
                            {trendScoresCategory.value === "All" && data.questions ? (
                                data.questions.map((q: string, i: number) => {
                                  return (
                                      <Area
                                          dataKey={q}
                                          fill={chartColours[i]}
                                          isAnimationActive={false}
                                          type="monotone"
                                          stroke={chartColours[i]}
                                          dot={true}
                                          fillOpacity=".1"
                                          key={"TrendScoresArea" + i}
                                      >
                                        <LabelList
                                            dataKey={q}
                                            position="top"
                                            formatter={
                                              data && data.options.yAxisType === "Percentage"
                                                  ? percentageFormatter
                                                  : avgFormatter
                                            }
                                        />
                                      </Area>
                                  );
                                })
                            ) : (
                                <Area
                                    dataKey={trendScoresCategory.label}
                                    fill={chartColours[0]}
                                    isAnimationActive={false}
                                    type="monotone"
                                    stroke={chartColours[0]}
                                    dot={true}
                                    fillOpacity=".1"
                                >
                                  <LabelList
                                      dataKey={trendScoresCategory.label}
                                      position="top"
                                      formatter={
                                        data && data.options.yAxisType === "Percentage"
                                            ? percentageFormatter
                                            : avgFormatter
                                      }
                                  />
                                </Area>
                            )}
                          </AreaChart>
                      ) : trendScoresChartType === "Bar" ? (
                          <BarChart
                              data={data.otherChartData}
                              margin={{top: 20, bottom: 20, right: 20}}
                              maxBarSize={65}
                              barGap={15}
                              ref={ref}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3"/>
                            <Tooltip
                                formatter={
                                  data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : avgFormatter
                                }
                            />
                            <XAxis
                                dataKey="name"
                                tick={<TrendTick/>}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={
                                  data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : avgFormatter
                                }
                                domain={data.options.yAxisType === "Percentage" ? [0, 100] : undefined}
                                ticks={data.options.yAxisType === "Percentage" ? ticks20 : undefined}
                            />
                            {trendScoresCategory.value === "All" && data.questions ? (
                                data.questions.map((q: string, i: number) => {
                                  return (
                                      <Bar
                                          dataKey={q}
                                          fill={chartColours[i]}
                                          radius={[3, 3, 0, 0]}
                                          isAnimationActive={false}
                                      >
                                        <LabelList
                                            dataKey={q}
                                            position="top"
                                            formatter={
                                              data && data.options.yAxisType === "Percentage"
                                                  ? percentageFormatter
                                                  : avgFormatter
                                            }
                                        />
                                      </Bar>
                                  );
                                })
                            ) : (
                                <Bar
                                    dataKey={trendScoresCategory.label}
                                    fill={chartColours[0]}
                                    radius={[3, 3, 0, 0]}
                                    isAnimationActive={false}
                                >
                                  <LabelList
                                      dataKey={trendScoresCategory.label}
                                      position="top"
                                      formatter={
                                        data && data.options.yAxisType === "Percentage"
                                            ? percentageFormatter
                                            : avgFormatter
                                      }
                                  />
                                </Bar>
                            )}
                          </BarChart>
                      ) : trendScoresChartType === "Line" ? (
                          <LineChart
                              data={data.otherChartData}
                              margin={{top: 20, bottom: 20, right: 20}}
                              ref={ref}
                          >
                            <CartesianGrid vertical={false} strokeDasharray="3"/>
                            <Tooltip
                                formatter={
                                  data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : avgFormatter
                                }
                            />
                            <XAxis
                                dataKey="name"
                                tick={<TrendTick/>}
                                axisLine={false}
                                tickLine={false}
                                padding={{left: 50, right: 50}}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={
                                  data.options.yAxisType === "Percentage"
                                      ? percentageFormatter
                                      : avgFormatter
                                }
                                domain={data.options.yAxisType === "Percentage" ? [0, 100] : undefined}
                                ticks={data.options.yAxisType === "Percentage" ? ticks20 : undefined}
                            />
                            {trendScoresCategory.value === "All" && data.questions ? (
                                data.questions.map((q: string, i: number) => {
                                  return (
                                      <Line
                                          key={i}
                                          dataKey={q}
                                          fill={chartColours[i]}
                                          isAnimationActive={false}
                                          type="monotone"
                                          stroke={chartColours[i]}
                                          dot={{
                                            stroke: chartColours[i],
                                            strokeWidth: 2,
                                          }}
                                      >
                                        <LabelList
                                            dataKey={q}
                                            position="top"
                                            formatter={
                                              data && data.options.yAxisType === "Percentage"
                                                  ? percentageFormatter
                                                  : avgFormatter
                                            }
                                        />
                                      </Line>
                                  );
                                })
                            ) : (
                                <Line
                                    dataKey={trendScoresCategory.label}
                                    fill={chartColours[0]}
                                    isAnimationActive={false}
                                    type="monotone"
                                    stroke={chartColours[0]}
                                    dot={{
                                      stroke: chartColours[0],
                                      strokeWidth: 2,
                                    }}
                                >
                                  <LabelList
                                      dataKey={trendScoresCategory.label}
                                      position="top"
                                      formatter={
                                        data && data.options.yAxisType === "Percentage"
                                            ? percentageFormatter
                                            : avgFormatter
                                      }
                                  />
                                </Line>
                            )}
                          </LineChart>
                      ) : (
                          <React.Fragment/>
                      )}
                    </ResponsiveContainer>
                  </div>
              ) : (
                  <div className="NoChartData">
                    <p>No results found</p>
                  </div>
              )}
            </div>
          </div>
        </div>
      </Card>
  );
};

export default TrendScores;